import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { runInventoryReconciliation } from "@/lib/inventory-reconciliation.functions";
import {
  escalateDriftToCycleCount,
  resolveDriftWithAdjustment,
} from "@/lib/drift-resolve.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { DataTable, type ColumnDef } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Play,
  AlertTriangle,
  CheckCircle2,
  MoreHorizontal,
  ClipboardCheck,
  Wrench,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/app/admin/reconciliation")({
  component: ReconciliationAdmin,
  validateSearch: z.object({ tenant: z.string().uuid().optional() }),
});

type DriftRow = {
  tenant_id: string;
  facility_id: string;
  sku_id: string;
  location_id: string;
  lot: string;
  ledger_qty: number;
  on_hand_qty: number;
  drift: number;
};

function ReconciliationAdmin() {
  const run = useServerFn(runInventoryReconciliation);
  const escalate = useServerFn(escalateDriftToCycleCount);
  const adjust = useServerFn(resolveDriftWithAdjustment);
  const { tenants, tenantId: scopeTenantId, loading: scopeLoading } = useScope();
  const { tenant: tenantFromUrl } = Route.useSearch();
  const [tenantId, setTenantId] = useState<string>("");
  const [facilityId, setFacilityId] = useState("");
  const [result, setResult] = useState<{ runAt: string; driftCount: number; rows: DriftRow[] } | null>(null);
  const [resolveTarget, setResolveTarget] = useState<{
    row: DriftRow;
    mode: "adjust" | "accept";
  } | null>(null);
  const [reason, setReason] = useState<string>("system_correction");
  const [note, setNote] = useState("");

  // Prefer ?tenant= from URL, then the current scope tenant, then the first tenant
  useEffect(() => {
    if (!tenantId && tenantFromUrl) setTenantId(tenantFromUrl);
    else if (!tenantId && scopeTenantId) setTenantId(scopeTenantId);
    else if (!tenantId && tenants[0]) setTenantId(tenants[0].id);
  }, [tenantId, tenantFromUrl, scopeTenantId, tenants]);

  const mutation = useMutation({
    mutationFn: () =>
      run({
        data: {
          tenantId: tenantId.trim(),
          facilityId: facilityId.trim() || null,
          limit: 500,
        },
      }),
    onSuccess: (data) => {
      setResult(data as never);
      if ((data as { driftCount: number }).driftCount === 0) {
        toast.success("Reconciliation clean — no drift");
      } else {
        toast.warning(`Drift detected on ${(data as { driftCount: number }).driftCount} buckets`);
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const removeRow = (r: DriftRow) =>
    setResult((prev) =>
      prev
        ? {
            ...prev,
            rows: prev.rows.filter(
              (x) =>
                !(
                  x.sku_id === r.sku_id &&
                  x.location_id === r.location_id &&
                  (x.lot || "") === (r.lot || "")
                ),
            ),
            driftCount: Math.max(0, prev.driftCount - 1),
          }
        : prev,
    );

  const escalateMut = useMutation({
    mutationFn: (r: DriftRow) =>
      escalate({
        data: {
          tenantId: r.tenant_id,
          facilityId: r.facility_id,
          skuId: r.sku_id,
          locationId: r.location_id,
          lot: r.lot || null,
          ledgerQty: r.ledger_qty,
          onHandQty: r.on_hand_qty,
        },
      }),
    onSuccess: (_d, r) => {
      toast.success("Cycle count opened");
      removeRow(r);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const adjustMut = useMutation({
    mutationFn: () => {
      if (!resolveTarget) throw new Error("no target");
      const r = resolveTarget.row;
      const effectiveReason =
        resolveTarget.mode === "accept" ? "historical_variance_accepted" : reason;
      return adjust({
        data: {
          tenantId: r.tenant_id,
          facilityId: r.facility_id,
          skuId: r.sku_id,
          locationId: r.location_id,
          lot: r.lot || null,
          ledgerQty: r.ledger_qty,
          onHandQty: r.on_hand_qty,
          reason: effectiveReason as
            | "damage"
            | "shrinkage"
            | "found"
            | "system_correction"
            | "historical_variance_accepted",
          note: note.trim(),
        },
      });
    },
    onSuccess: () => {
      toast.success(
        resolveTarget?.mode === "accept"
          ? "Variance accepted and posted"
          : "Adjustment posted",
      );
      if (resolveTarget) removeRow(resolveTarget.row);
      setResolveTarget(null);
      setNote("");
      setReason("system_correction");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const columns: ColumnDef<DriftRow, unknown>[] = [
    { id: "sku",    header: "SKU",      cell: ({ row }) => <span className="font-mono text-xs">{row.original.sku_id.slice(0, 8)}</span> },
    { id: "loc",    header: "Location", cell: ({ row }) => <span className="font-mono text-xs">{row.original.location_id.slice(0, 8)}</span> },
    { id: "lot",    header: "Lot",      cell: ({ row }) => row.original.lot || "—" },
    { id: "ledger", header: "Ledger",   cell: ({ row }) => <span className="tabular-nums">{row.original.ledger_qty.toLocaleString()}</span> },
    { id: "oh",     header: "On hand",  cell: ({ row }) => <span className="tabular-nums">{row.original.on_hand_qty.toLocaleString()}</span> },
    {
      id: "drift", header: "Drift",
      cell: ({ row }) => (
        <Badge variant={row.original.drift === 0 ? "secondary" : "destructive"}>
          {row.original.drift > 0 ? "+" : ""}
          {row.original.drift.toLocaleString()}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const r = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Resolve drift</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => escalateMut.mutate(r)}
                disabled={escalateMut.isPending}
              >
                <ClipboardCheck className="mr-2 h-4 w-4" />
                Open cycle count
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setResolveTarget({ row: r, mode: "adjust" });
                  setReason("system_correction");
                  setNote("");
                }}
              >
                <Wrench className="mr-2 h-4 w-4" />
                Post adjustment…
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  setResolveTarget({ row: r, mode: "accept" });
                  setNote("");
                }}
              >
                <ShieldCheck className="mr-2 h-4 w-4" />
                Accept variance (manager)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Inventory Reconciliation"
        description="Daily ledger-vs-on-hand drift check. Any non-zero drift is a data-integrity bug — either a write bypassed the ledger or the on-hand projection fell out of sync."
      />

      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Tenant</label>
            <Select value={tenantId} onValueChange={setTenantId} disabled={scopeLoading || tenants.length === 0}>
              <SelectTrigger>
                <SelectValue placeholder={scopeLoading ? "Loading tenants…" : "Select a tenant"} />
              </SelectTrigger>
              <SelectContent>
                {tenants.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Facility ID (optional)</label>
            <Input value={facilityId} onChange={(e) => setFacilityId(e.target.value)} placeholder="uuid" />
          </div>
          <div className="flex items-end">
            <Button
              onClick={() => mutation.mutate()}
              disabled={!tenantId || mutation.isPending}
              className="w-full"
            >
              {mutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              Run reconciliation
            </Button>
          </div>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            {result.driftCount === 0 ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>Clean. Ran at {new Date(result.runAt).toLocaleString()}.</span>
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 text-warning" />
                <span>
                  <strong>{result.driftCount}</strong> drift buckets — ran at {new Date(result.runAt).toLocaleString()}
                </span>
              </>
            )}
          </div>
          {result.rows.length > 0 && <DataTable data={result.rows} columns={columns} />}
        </div>
      )}

      <Dialog
        open={!!resolveTarget}
        onOpenChange={(open) => {
          if (!open) {
            setResolveTarget(null);
            setNote("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {resolveTarget?.mode === "accept"
                ? "Accept historical variance"
                : "Post adjustment"}
            </DialogTitle>
            <DialogDescription>
              {resolveTarget &&
                `Delta posted: ${
                  resolveTarget.row.on_hand_qty - resolveTarget.row.ledger_qty
                } (ledger ${resolveTarget.row.ledger_qty} → on-hand ${resolveTarget.row.on_hand_qty})`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {resolveTarget?.mode === "adjust" && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Reason code</label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="damage">Damage</SelectItem>
                    <SelectItem value="shrinkage">Shrinkage</SelectItem>
                    <SelectItem value="found">Found stock</SelectItem>
                    <SelectItem value="system_correction">System correction</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Note {resolveTarget?.mode === "accept" ? "(required — justification)" : ""}
              </label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder={
                  resolveTarget?.mode === "accept"
                    ? "e.g. Seed/migration artifact, confirmed by ops lead"
                    : "What happened, who/when noticed it"
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setResolveTarget(null);
                setNote("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => adjustMut.mutate()}
              disabled={!note.trim() || adjustMut.isPending}
              variant={resolveTarget?.mode === "accept" ? "destructive" : "default"}
            >
              {adjustMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {resolveTarget?.mode === "accept" ? "Accept variance" : "Post adjustment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}